import React from "react";
import CustomSelect from "../../../../components/custom-select/custom-select";
import FallbackImage from "../../../../components/Common/ImageWrapper";
import CurrencyInput from "react-currency-input-field";
import { formatMoney } from "../../../../utils";
import { idsMatch } from "../../../../utils/resolveSelectValue";

const MemberRecordContributionForm = ({
  index,
  formik,
  handleRemove,
  formattedContributionCategories,
}) => {
  const handleInputChange = (fieldName, value) => {
    formik.setFieldValue(`formParts[${index}].${fieldName}`, value);
  };

  // Filter out already selected contributions from other form parts
  // Keep the currently selected value in the dropdown
  const availableContributionCategories = React.useMemo(() => {
    if (
      !formattedContributionCategories ||
      !Array.isArray(formattedContributionCategories)
    )
      return [];

    const currentCategoryId =
      formik.values.formParts[index]?.contribution_category_id || "";

    // Get all selected contribution category IDs from OTHER form parts (exclude current)
    const selectedCategoryIds = formik.values.formParts
      .map((part, idx) => {
        if (idx === index) return null; // Exclude current form part
        return part?.contribution_category_id || "";
      })
      .filter((id) => id && id !== "" && id !== null)
      .map((id) => String(id));

    // Filter out already selected contributions, but keep the current selection
    return formattedContributionCategories.filter((category) => {
      const categoryValue = String(category?.value ?? "");
      return (
        !selectedCategoryIds.includes(categoryValue) ||
        idsMatch(categoryValue, currentCategoryId)
      );
    });
  }, [formattedContributionCategories, formik.values.formParts, index]);

  const selectedContributionId =
    formik.values.formParts[index]?.contribution_category_id;

  const selectedContributionOption = React.useMemo(
    () =>
      availableContributionCategories?.find((opt) =>
        idsMatch(opt.value, selectedContributionId)
      ) || null,
    [availableContributionCategories, selectedContributionId]
  );

  return (
    <section className="flex gap-4 pb-6 border-b border-gray2 last:border-0">
      <div className="flex flex-col gap-3 justify-start items-center flex-shrink-0">
        <div className="h-10 w-10 rounded-full border-2 border-gray2 bg-white flex justify-center items-center fw-semibold text-black1">
          {index + 1}
        </div>
        {index < formik.values.formParts.length - 1 && (
          <div className="h-full min-h-[120px] border-l-2 border-dashed border-gray2" />
        )}
      </div>

      <section className="w-full grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-0 relative flex-1">
        <div>
          <CustomSelect
            label="Contribution name"
            options={availableContributionCategories}
            value={selectedContributionId || null}
            onBlur={formik.handleBlur}
            onChange={(value) =>
              handleInputChange("contribution_category_id", value?.value || "")
            }
          />

          {formik.errors.formParts &&
            formik.errors.formParts[index]?.contribution_category_id && (
              <p className="text-danger small mt-1">
                {formik.errors.formParts[index].contribution_category_id}
              </p>
            )}
        </div>

        <div className="my-[10px]">
          <p className="text-[#1E1E1E] font-medium mb-1">Amount</p>
          <CurrencyInput
            id={`amount-${index}`}
            name={`amount-${index}`}
            placeholder="Please enter an amount"
            value={formik.values.formParts[index]?.amount || ""}
            decimalsLimit={2}
            prefix="NGN "
            className="h-10 bg-white2 cursor-text w-full border border-gray2 pl-3 text-black1 leading-[24px] text-[1rem] outline-none rounded-[5px] placeholder:text-[14px] placeholder:text-blacklight focus:border-primary1 focus:ring-1 focus:ring-primary1 disabled:opacity-50"
            onValueChange={(value) => handleInputChange("amount", value)}
          />

          {formik.errors.formParts &&
            formik.errors.formParts[index]?.amount && (
              <p className="text-danger small mt-1">
                {formik.errors.formParts[index].amount}
              </p>
            )}
          {selectedContributionOption?.maxAmount && (
            <p className="text-muted small mt-1">
              Maximum: {formatMoney(selectedContributionOption.maxAmount)}
            </p>
          )}
        </div>

        {index !== 0 && (
          <button
            type="button"
            onClick={() => handleRemove(index)}
            className="h-10 w-10 border-0 absolute top-0 right-0 rounded-circle bg-danger d-flex align-items-center justify-content-center hover:opacity-90 transition-opacity"
            style={{ transform: "translateX(50%)" }}
            aria-label="Remove contribution"
          >
            <FallbackImage src="/Icon/delete1.png" width={20} height={20} />
          </button>
        )}
      </section>
    </section>
  );
};

export default MemberRecordContributionForm;
